// ==========================================
// 3. SCOOTER & PHYSICS CONTROLLER
// ==========================================
export class Scooter {
  constructor(startX, startY) {
    this.x = startX;
    this.y = startY;
    this.angle = -Math.PI / 2; // Facing up initially
    this.speed = 0;
    this.steerAngle = 0;
    this.angularVelocity = 0;
    this.lateralVelocity = 0;

    // Configurable attributes (Upgrades & Tuning - Fase 4)
    this.vehicleType = 'scooter'; // 'scooter' | 'van'
    this.boxType = 'geenbak'; // 'geenbak' | 'kleinebak' | 'grotebak'
    this.hasSportVelgen = false;
    this.hasTurbo = false;
    this.paintColor = '#dc2626';

    // Physics parameters (Calm, highly controllable city cruising)
    this.maxSpeed = 3.2;        // Controlled maximum speed (~27 km/u, perfectly readable and never flies off-screen)
    this.maxReverse = -1.3;     // Gentle, easy reversing speed
    this.accel = 0.085;         // Smooth, natural acceleration
    this.brake = 0.24;          // Crisp, reassuring braking
    this.friction = 0.982;      // Smooth coasting friction
    this.turnRate = 0.052;      // Agile, immediate steering response
    this.lateralGrip = 0.96;    // High tire traction (direct road tracking, zero unwanted sliding)

    // Visual & State effects
    this.bumpOffset = 0;
    this.bumpTimer = 0;
    this.isDrifting = false;
    this.inPuddle = false;
    this.exhaustParticles = [];
    this.skidMarks = []; // Buffer of skid segments
    this.maxSkids = 600;

    // Jump ramp & airborne stunt physics (Schanssprong over de geopende ophaalbrug)
    this.jumpZ = 0;             // Hoogte in de lucht in pixels (0 = op het wegdek)
    this.jumpVZ = 0;            // Verticale lanceersnelheid
    this.isAirborne = false;    // Vliegt door de lucht over de gracht
    this.stuntAirTime = 0;      // Hangtijd in de lucht
    this.stuntLaunchSide = null;// 'west' of 'east'
    this.stuntRewardGiven = false;
    this.onStuntJump = null;    // Callback naar BezorgerGame voor beloning en audio

    // Inputs & Analog Stick controls
    this.keys = {
      up: false,
      down: false,
      left: false,
      right: false,
      handbrake: false
    };
    this.analogSteer = 0;    // -1.0 (vol links) tot +1.0 (vol rechts)
    this.analogThrottle = 0; // -1.0 (achteruit/rem) tot +1.0 (gas)
    this.directStickActive = false; // 360° Richting-stick modus
    this.targetDirectAngle = null;  // Gewenste hoek bij 360° stick
    this.brakeHoldTimer = 0;        // Timer voor soepele scheiding tussen remmen en achteruit
    this.totalDistanceMeters = 0;

    // Preloaded scooter images
    this.sprites = {};
    this.loadSprites();
  }

  loadSprites() {
    const filenames = {
      'geenbak_geenvelg': '/img/scooter_geenbak_geenvelg_klein.png',
      'geenbak_velg': '/img/scooter_geenbak_velg_klein.png',
      'kleinebak_geenvelg': '/img/scooter_kleinebak_geenvelg_klein.png',
      'kleinebak_velg': '/img/scooter_kleinebak_velg_klein.png',
      'grotebak_geenvelg': '/img/scooter_grotebak_geenvelg_klein.png',
      'grotebak_velg': '/img/scooter_grotebak_velg_klein.png',
      'cat': '/img/cat_klein.png'
    };

    for (const [key, url] of Object.entries(filenames)) {
      const img = new Image();
      img.src = url;
      this.sprites[key] = img;
    }
  }

  getCurrentSpriteKey() {
    const velg = this.hasSportVelgen ? 'velg' : 'geenvelg';
    return `${this.boxType}_${velg}`;
  }

  setVehicleType(type) {
    this.vehicleType = (type === 'van') ? 'van' : 'scooter';
    if (this.vehicleType === 'van') {
      // Bakkerij Express Bezorgbus: robuuster, stabieler, meer topsnelheid en koppel
      this.maxSpeed = 3.65;
      this.maxReverse = -1.2;
      this.accel = 0.095;
      this.brake = 0.28;
      this.turnRate = 0.042; // Iets bredere draaicirkel passend bij een busje
      this.friction = 0.985;
      this.lateralGrip = 0.97;
    } else {
      // Klassieke behendige scooter
      this.maxSpeed = 3.2;
      this.maxReverse = -1.3;
      this.accel = 0.085;
      this.brake = 0.24;
      this.turnRate = 0.052;
      this.friction = 0.982;
      this.lateralGrip = 0.96;
    }
  }

  update(dt, cityMap, soundEngine, weather = 'sunny') {
    // 1. Steering & Turn mechanics (Arcade Knoppen / Toetsenbord / 360° Richting-stick)
    const absSpeed = Math.abs(this.speed);

    // Optie A: 360° Richting-Joystick (Speler wijst direct waar hij naartoe wil sturen)
    if (this.directStickActive && this.targetDirectAngle !== null) {
      // Bereken kortste hoekverschil tussen huidige richting en richtingsstick
      let diff = this.targetDirectAngle - this.angle;
      while (diff < -Math.PI) diff += Math.PI * 2;
      while (diff > Math.PI) diff -= Math.PI * 2;

      // Wendbaarheid aangepast aan voertuigtype
      const turnSpeed = this.vehicleType === 'van' ? 0.088 : 0.115;
      if (Math.abs(diff) > 0.02) {
        this.angle += Math.sign(diff) * Math.min(Math.abs(diff), turnSpeed);
        this.steerAngle = Math.sign(diff) * Math.min(0.42, Math.abs(diff) * 0.6);
      } else {
        this.angle = this.targetDirectAngle;
        this.steerAngle *= 0.6;
      }
    } else {
      // Optie B: Klassieke Arcade Besturing (Grote Knoppen ◀ ▶ of A/D & Pijltjestoetsen)
      let steerInput = 0;
      if (Math.abs(this.analogSteer) > 0.04) {
        steerInput = this.analogSteer; // Vloeiende fractie tussen -1.0 en 1.0
      } else if (this.keys.left) {
        steerInput = -1.0;
      } else if (this.keys.right) {
        steerInput = 1.0;
      }

      // Digitale invoer (toetsen, knoppen) loopt in ~0,1 s op naar vol: een kort tikje is een kleine correctie,
      // vasthouden is een volle bocht. Loslaten centreert sneller dan insturen.
      const prevSmooth = this.steerSmooth || 0;
      this.steerSmooth = prevSmooth + (steerInput - prevSmooth) * (steerInput === 0 ? 0.35 : 0.22);
      if (steerInput === 0 && Math.abs(this.steerSmooth) < 0.03) this.steerSmooth = 0;
      steerInput = this.steerSmooth;

      if (steerInput !== 0) {
        // Ackermann-geïnspireerde snelheidsgevoeligheid:
        // - Bij stilstand (< 0.15): geen tol meer op de stip, slechts een subtiele stuurknik
        // - Bij stadsrijden (0.8 - 2.5): haarscherpe 90-graden bochten om straathoeken
        // - Bij topsnelheid (> 3.0): zachte stabiliserende demping voor strakke koers
        let speedFactor = 0;
        if (absSpeed < 0.15) {
          speedFactor = 0.08; // Minimaal sturen bij stilstand/kruipen
        } else if (absSpeed < 1.2) {
          speedFactor = 0.25 + (absSpeed / 1.2) * 0.75;
        } else {
          speedFactor = 1.0 / (1.0 + (absSpeed - 1.2) * 0.16);
        }

        const baseTurn = (this.vehicleType === 'van' ? 0.044 : 0.052);
        const effectiveTurn = baseTurn * speedFactor;
        const steerDir = this.speed >= 0 ? 1 : -1;

        this.angle += effectiveTurn * steerDir * steerInput;
        this.steerAngle = Math.max(-0.45, Math.min(0.45, this.steerAngle + 0.18 * steerInput));
      } else {
        // Vloeiende, natuurlijke zelfcentrering van het stuur
        this.steerAngle *= 0.65;

        // Stuurhulp: zonder stuurinvoer trekt de scooter zachtjes recht langs de straat.
        // Het stratenplan is een raster, dus "recht" is altijd een veelvoud van 90 graden.
        if (this.steerAssist && absSpeed > 0.6 && !this.keys.handbrake) {
          const quarter = Math.PI / 2;
          const diff = Math.round(this.angle / quarter) * quarter - this.angle;
          if (Math.abs(diff) < 0.4) this.angle += diff * 0.07;
        }
      }
    }

    // Resolve any collision when turning against a wall (prevents nose/tail clipping)
    cityMap.resolveScooterCollision(this);

    // 2. Weather Physics Modifiers (Regen, Mist & Zonnig)
    let weatherSpeedMult = 1.0;
    let weatherAccelMult = 1.0;
    let weatherBrakeMult = 1.0;
    let weatherGripMult = 1.0;

    if (weather === 'rain') {
      weatherSpeedMult = 0.92;  // Capped slightly by wet road drag & rolling resistance
      weatherAccelMult = 0.88;  // Minor wheelspin on high-throttle start
      weatherBrakeMult = 0.68;  // Significantly longer braking distance in rain
      weatherGripMult = 0.74;   // Slippery road surface
    } else if (weather === 'mist') {
      weatherSpeedMult = 0.96;
      weatherAccelMult = 0.94;
      weatherBrakeMult = 0.84;
      weatherGripMult = 0.89;   // Damp road condensation
    }

    // 2b. Acceleration & Braking (Pedaal of Stick omhoog/omlaag)
    const isThrottling = this.keys.up || (this.analogThrottle > 0.16);
    const isBraking = this.keys.down || (this.analogThrottle < -0.16);

    // Na een geslaagde drift: korte boost (hogere topsnelheid en vlotter optrekken)
    const boosting = (this.boostTimer || 0) > 0;
    if (boosting) this.boostTimer -= dt || 1 / 60;
    // Het busje is breed en zwaar: op stoep en gras haalt het maar de helft van zijn snelheid.
    // De scooter kan juist afsnijden. (Zo heeft het busje met zijn grote laadruimte een echte keerzijde.)
    this.offRoad = this.vehicleType === 'van' && !cityMap.isPointOnRoad(this.x, this.y).onRoad;
    const currentMaxSpeed = (this.hasTurbo ? 4.25 : this.maxSpeed) * weatherSpeedMult * (boosting ? 1.18 : 1) * (this.offRoad ? 0.5 : 1); // ~36 km/u with Turbo
    const currentAccel = (this.hasTurbo ? 0.125 : this.accel) * weatherAccelMult * (boosting ? 1.6 : 1);
    const currentBrake = this.brake * weatherBrakeMult;

    if (isThrottling) {
      this.brakeHoldTimer = 0;
      const throttleFactor = (this.analogThrottle > 0.16) ? Math.min(1.0, this.analogThrottle) : 1.0;
      if (this.speed < currentMaxSpeed) {
        this.speed = Math.min(currentMaxSpeed, this.speed + currentAccel * throttleFactor);
      }
    } else if (isBraking) {
      const brakeFactor = (this.analogThrottle < -0.16) ? Math.min(1.0, -this.analogThrottle) : 1.0;
      if (this.speed > 0) {
        // Directe rem aangepast aan wegdek en grip - stopt exact en strak bij 0
        this.speed = Math.max(0, this.speed - currentBrake * brakeFactor);
        this.brakeHoldTimer = 0;
      } else {
        // Voertuig staat stil: scheid remmen van achteruitrijden
        this.brakeHoldTimer = (this.brakeHoldTimer || 0) + (dt || 0.016);
        if (this.brakeHoldTimer > 0.08) {
          // Rustig achteruit rijden na bewuste pedaaldruk
          this.speed = Math.max(this.maxReverse, this.speed - this.accel * 0.75 * brakeFactor);
        }
      }
    } else {
      this.brakeHoldTimer = 0;
      // Natuurlijke rolweerstand
      this.speed *= this.friction;
      if (Math.abs(this.speed) < 0.03) this.speed = 0;
    }

    // Busje rijdt de stoep of het gras op: snel afremmen tot de lagere topsnelheid
    if (this.offRoad && this.speed > currentMaxSpeed) this.speed = Math.max(currentMaxSpeed, this.speed * 0.94);

    // 3. Road Grip & Drift Handling (Volwaardige Arcade Oversteer & Drift Fysica)
    let baseGrip = this.hasSportVelgen ? 0.985 : this.lateralGrip;
    let grip = this.hasSportVelgen
      ? (weather === 'rain' ? 0.88 : (weather === 'mist' ? 0.94 : 0.985))
      : (baseGrip * weatherGripMult);
    this.currentGripPercent = Math.round(grip * 100);

    const hasSteerInput = (Math.abs(this.analogSteer) > 0.08 || this.keys.left || this.keys.right || Math.abs(this.steerAngle) > 0.12);
    const isDriftKeyActive = this.keys.handbrake;

    if (isDriftKeyActive && Math.abs(this.speed) > 0.8) {
      if (hasSteerInput) {
        // DRIFT / OVERSTEER ACTIVE: Verlaag achterband-frictie met 60% (grip = 0.40)
        grip *= 0.40;
        this.isDrifting = true;

        // Sneller roteren dan verplaatsingsvector (oversteer yaw rate)
        const steerDir = Math.sign(this.steerAngle || this.analogSteer || (this.keys.left ? -1 : 1));
        this.angle += steerDir * 0.045;
        this.lateralVelocity += steerDir * this.speed * 0.22;

        // Drift score & dynamische multiplier opbouwen (1.0x tot 3.0x)
        const frameDt = dt || 0.016;
        this.driftScore = (this.driftScore || 0) + frameDt * 160 * Math.max(1, Math.abs(this.speed));
        this.driftMultiplier = Math.min(3.0, 1.0 + (this.driftScore / 220) * 0.5);
        this.driftBonusTip = Math.round(this.driftScore * 0.004 * 100) / 100;

        // Dense smoke & friction spark particles bij achterbanden.
        // Eigen richtingsvector: forwardX/forwardY worden pas verderop in update() gedeclareerd,
        // en gebruik vóór die regel liet het spel vastlopen zodra je driftte.
        const driftFx = Math.cos(this.angle);
        const driftFy = Math.sin(this.angle);
        const rearWheelX = this.x - driftFx * 20;
        const rearWheelY = this.y - driftFy * 20;
        if (Math.random() < 0.65) {
          this.exhaustParticles.push({
            x: rearWheelX + (Math.random() - 0.5) * 8,
            y: rearWheelY + (Math.random() - 0.5) * 8,
            vx: -driftFx * 0.4 + (Math.random() - 0.5) * 1.6,
            vy: -driftFy * 0.4 + (Math.random() - 0.5) * 1.6,
            radius: 3 + Math.random() * 4,
            alpha: 0.75,
            life: 0.6,
            isSmoke: true
          });
        }
        if (Math.random() < 0.4) {
          this.exhaustParticles.push({
            x: rearWheelX + (Math.random() - 0.5) * 6,
            y: rearWheelY + (Math.random() - 0.5) * 6,
            vx: (Math.random() - 0.5) * 3.5,
            vy: (Math.random() - 0.5) * 3.5,
            radius: 1.5 + Math.random() * 2,
            alpha: 0.9,
            life: 0.35,
            isSpark: true
          });
        }
      } else {
        // Rechte handrem: gecontroleerde remvertraging
        this.speed *= 0.95;
        grip = 0.60;
        this.isDrifting = false;
      }
    } else if (weather === 'rain' && !this.hasSportVelgen && Math.abs(this.steerAngle) > 0.22 && Math.abs(this.speed) > 1.6) {
      // Regen-aquaplaning zonder sportvelgen
      this.isDrifting = true;
      this.lateralVelocity += this.steerAngle * this.speed * 0.085;
    } else {
      // Drift beëindigd: een geslaagde drift geeft een korte boost; hoe langer de drift, hoe langer de boost.
      if (this.isDrifting && (this.driftScore || 0) > 35) {
        this.boostTimer = Math.min(1.2, 0.4 + this.driftScore / 300);
        if (typeof this.onDriftBoost === 'function') this.onDriftBoost(this.boostTimer);
      }
      this.isDrifting = false;
      this.driftScore = 0;
      this.driftBonusTip = 0;
      this.lateralVelocity = 0;
    }

    // Water puddle hydroplaning (Sportvelgen resist puddle hydroplaning!)
    const puddle = cityMap.checkPuddle(this.x, this.y);
    if (puddle) {
      this.inPuddle = true;
      grip = this.hasSportVelgen ? 0.84 : (weather === 'rain' ? 0.38 : 0.45);
    } else {
      this.inPuddle = false;
    }

    // Zandgraafwerkzaamheden / sleuven bij wegwerkzaamheden vertragen het voertuig
    const sandTrench = cityMap.checkSandTrench && cityMap.checkSandTrench(this.x, this.y);
    if (sandTrench) {
      if (Math.abs(this.speed) > 1.2) {
        this.speed *= 0.94; // Zand/grind weerstand
      }
      grip *= 0.72;
    }

    // Directional move vectors
    const forwardX = Math.cos(this.angle);
    const forwardY = Math.sin(this.angle);
    const rightX = -Math.sin(this.angle);
    const rightY = Math.cos(this.angle);

    this.lateralVelocity *= grip;

    const isTireScreeching = this.isDrifting || (this.keys.down && this.speed > 2.0);
    soundEngine.setDriftSound(isTireScreeching || this.inPuddle);

    // Apply movement
    const stepDist = Math.hypot(forwardX * this.speed + rightX * this.lateralVelocity, forwardY * this.speed + rightY * this.lateralVelocity);
    this.totalDistanceMeters = (this.totalDistanceMeters || 0) + stepDist * 0.28;
    this.x += forwardX * this.speed + rightX * this.lateralVelocity;
    this.y += forwardY * this.speed + rightY * this.lateralVelocity;

    // 3b. Schanssprong Dynamiek & Airborne Vlucht (Over de geopende Ophaalbrug)
    const frameDt = dt || 0.016;
    if (this.isAirborne) {
      this.stuntAirTime = (this.stuntAirTime || 0) + frameDt;
      // Zwaartekracht trekt scooter omlaag
      this.jumpVZ -= 14.2 * frameDt;
      this.jumpZ += this.jumpVZ * (frameDt * 60) * 0.48;

      // Stunt wind- en vonkendeeltjes
      if (Math.random() < 0.65) {
        this.exhaustParticles.push({
          x: this.x + (Math.random() - 0.5) * 12,
          y: this.y + (Math.random() - 0.5) * 12,
          vx: -forwardX * 0.6 + (Math.random() - 0.5) * 1.6,
          vy: -forwardY * 0.6 + (Math.random() - 0.5) * 1.6,
          radius: 2.5 + Math.random() * 2.5,
          alpha: 0.9,
          life: 0.45,
          isSpark: true
        });
      }

      // Landing / Touchdown
      if (this.jumpZ <= 0) {
        this.jumpZ = 0;
        this.jumpVZ = 0;
        this.isAirborne = false;
        this.bumpOffset = -4.5; // Verende landing
        if (soundEngine && soundEngine.playBump) soundEngine.playBump();

        // Controleer of de speler succesvol over de Prinsengracht is gesprongen
        const isOverWestSide = (this.x < 2420);
        const isOverEastSide = (this.x > 2540);
        const crossedCanal = (this.stuntLaunchSide === 'west' && isOverEastSide) ||
                             (this.stuntLaunchSide === 'east' && isOverWestSide);

        if (crossedCanal && !this.stuntRewardGiven && this.stuntAirTime > 0.22) {
          this.stuntRewardGiven = true;
          if (typeof this.onStuntJump === 'function') {
            this.onStuntJump(this.stuntAirTime);
          }
        }
        this.stuntLaunchSide = null;
      }
    } else {
      // Schansdetectie: Controleer of speler een van de 2 opstaande brugvallen (schansen) oprijdt met snelheid
      const bridge = (cityMap && cityMap.drawBridges) ? cityMap.drawBridges[0] : null;
      if (bridge && (bridge.openProgress > 0.10 || bridge.state === 'opening' || bridge.state === 'open')) {
        const bProg = Math.max(0.18, bridge.openProgress);
        const onBridgeY = (this.y >= bridge.deckY + 12 && this.y <= bridge.deckY + bridge.deckH - 12);
        const halfSpan = bridge.deckW / 2; // 60px
        const leafWidth = Math.max(10, halfSpan * (1 - bProg * 0.78));

        if (onBridgeY) {
          // 1. Vanaf de Westelijke weg (X=2420) over de Westelijke schans richting het Oosten (gap)
          const atWestRamp = (this.x >= bridge.deckX - 16 && this.x <= bridge.deckX + leafWidth + 14);
          const headingEast = (forwardX > 0.28 && this.speed > 1.35);

          if (atWestRamp && headingEast) {
            this.isAirborne = true;
            this.jumpZ = 2;
            this.jumpVZ = 3.6 + (Math.abs(this.speed) * 0.95);
            this.stuntAirTime = 0;
            this.stuntLaunchSide = 'west';
            this.stuntRewardGiven = false;
            if (soundEngine && soundEngine.playBump) soundEngine.playBump();
          }

          // 2. Vanaf de Oostelijke weg (X=2540) over de Oostelijke schans richting het Westen (gap)
          const atEastRamp = (this.x <= bridge.deckX + bridge.deckW + 16 && this.x >= bridge.deckX + bridge.deckW - leafWidth - 14);
          const headingWest = (forwardX < -0.28 && this.speed > 1.35);

          if (atEastRamp && headingWest) {
            this.isAirborne = true;
            this.jumpZ = 2;
            this.jumpVZ = 3.6 + (Math.abs(this.speed) * 0.95);
            this.stuntAirTime = 0;
            this.stuntLaunchSide = 'east';
            this.stuntRewardGiven = false;
            if (soundEngine && soundEngine.playBump) soundEngine.playBump();
          }
        }
      }
    }

    // 4. Solid Building Collisions (Cannot pass through buildings!)
    let shockEvent = null;
    const colResult = cityMap.resolveScooterCollision(this);
    if (colResult.hadCollision) {
      // Hoe frontaal was de klap? 1 = recht ertegenaan, 0 = langs de muur geschampt.
      const nLen = Math.hypot(colResult.normalX || 0, colResult.normalY || 0);
      // (ook een heel kleine duw heeft een geldige richting; alleen zonder richting, zoals de rand van
      // de wereld, rekenen we het als frontaal)
      const headOn = nLen > 1e-9
        ? Math.abs((Math.cos(this.angle) * colResult.normalX + Math.sin(this.angle) * colResult.normalY) / nLen)
        : 1;
      const impact = colResult.maxImpact * headOn;
      // Pas vanaf zo'n 8 km/u recht tegen de muur in telt het als klap; lichte schampjes niet.
      if (impact > 1.0) {
        soundEngine.playCrash(Math.min(1, impact / this.maxSpeed));
        this.bumpOffset = (Math.random() - 0.5) * Math.min(5, impact * 1.5);
        shockEvent = {
          type: 'collision',
          severity: Math.min(1.8, impact / (this.maxSpeed * 0.65)),
          message: '💥 Botsing tegen obstakel!'
        };
        // Collision debris / sparks
        for (let i = 0; i < 4; i++) {
          this.exhaustParticles.push({
            x: this.x + (Math.random() - 0.5) * 12,
            y: this.y + (Math.random() - 0.5) * 12,
            vx: (Math.random() - 0.5) * 2.5,
            vy: (Math.random() - 0.5) * 2.5,
            radius: 2 + Math.random() * 2,
            alpha: 0.8,
            life: 0.8,
            isSpark: true
          });
        }
      }
      // Frontaal: stilstaan. Schampen: snelheid grotendeels behouden en langs de muur glijden.
      // (kwadratisch: bij 15° schampen verlies je ~7%, bij 45° bijna de helft, frontaal alles)
      this.speed *= Math.max(0, 1 - headOn * headOn * 1.1) * 0.99;
      this.lateralVelocity = 0;
      if (nLen > 1e-9 && headOn < 0.75) {
        // Draai de neus mee met de muur, zodat je niet steeds opnieuw ertegenaan botst.
        const nx = colResult.normalX / nLen;
        const ny = colResult.normalY / nLen;
        let tx = -ny;
        let ty = nx;
        if (tx * Math.cos(this.angle) + ty * Math.sin(this.angle) < 0) {
          tx = -tx;
          ty = -ty;
        }
        let d = Math.atan2(ty, tx) - this.angle;
        while (d < -Math.PI) d += Math.PI * 2;
        while (d > Math.PI) d -= Math.PI * 2;
        this.angle += d * 0.35;
      }
    }

    // Dynamic Obstacle Collisions (Fase 2)
    // Traffic cone impact
    const hitCone = cityMap.checkConeCollision(this);
    if (hitCone && !shockEvent) {
      if (soundEngine && soundEngine.playConeKick) {
        soundEngine.playConeKick();
      } else if (soundEngine) {
        soundEngine.playBump();
      }
      if (this.vehicleType === 'van') {
        // Het bezorgbusje heeft een zware stalen bumper; pion vliegt weg zonder gebakschade!
        shockEvent = null;
      } else {
        shockEvent = {
          type: 'pion',
          severity: 0.28,
          message: '⚠️ Pion omver gereden!'
        };
      }
    }

    // Open van door impact (Knus Expres)
    const hitDoor = cityMap.checkDoorCollision(this);
    if (hitDoor && !shockEvent) {
      soundEngine.playCrash(0.85);
      this.bumpOffset = (Math.random() - 0.5) * 6;
      this.speed = -0.6;
      shockEvent = {
        type: 'portier',
        severity: 0.95,
        message: '🚪 Openslaand portier van Knus Expres geraakt!'
      };
    }

    // Botsing met carrosserie van rivaliserende Knus Expres bestelbus
    const hitVan = cityMap.checkVanCollision(this);
    if (hitVan && !shockEvent) {
      soundEngine.playCrash(Math.min(1.0, hitVan.relativeSpeed / 2.2));
      soundEngine.playCarHorn();
      this.bumpOffset = (Math.random() - 0.5) * 6;
      shockEvent = {
        type: 'auto',
        severity: Math.min(1.6, Math.max(0.6, hitVan.relativeSpeed / 1.8)),
        message: '⚡ Botsing met concurrent Knus Expres!'
      };
    }

    // Stadsverkeer auto impact
    const hitCar = cityMap.checkCarCollisions(this);
    if (hitCar && !shockEvent) {
      soundEngine.playCrash(Math.min(1.0, hitCar.relativeSpeed / 3.0));
      soundEngine.playCarHorn();
      this.bumpOffset = (Math.random() - 0.5) * 4;
      shockEvent = {
        type: 'auto',
        severity: Math.min(1.6, Math.max(0.4, hitCar.relativeSpeed / 2.0)),
        message: '🚗 Pas op voor het autoverkeer!'
      };
    }

    // Aanrijding met dorpskat (kat dood & politie gealarmeerd)
    const hitCat = cityMap.checkCatCollision(this);
    if (hitCat) {
      soundEngine.playCrash(0.65);
      soundEngine.playCatScreech();
      this.bumpOffset = (Math.random() - 0.5) * 6;
      shockEvent = {
        type: 'kat',
        cat: hitCat,
        severity: 1.3,
        message: `🚨 OEI! Dorpskat ${hitCat.name} aangereden!`
      };
    }

    // 5. Skid marks on asphalt
    if ((this.isDrifting || (this.keys.down && this.speed > 2)) && Math.abs(this.speed) > 1.2) {
      const rearWheelX = this.x - forwardX * 22;
      const rearWheelY = this.y - forwardY * 22;
      this.skidMarks.push({
        x: rearWheelX,
        y: rearWheelY,
        alpha: Math.min(0.65, (Math.abs(this.lateralVelocity) + 0.3) * 0.4)
      });
      if (this.skidMarks.length > this.maxSkids) {
        this.skidMarks.shift();
      }
    }

    // 6. Exhaust Particles & Water Spray when moving
    if (isThrottling && Math.abs(this.speed) > 0.6 && Math.random() < 0.55) {
      const exhaustX = this.x - forwardX * 28 + (Math.random() - 0.5) * 6;
      const exhaustY = this.y - forwardY * 28 + (Math.random() - 0.5) * 6;
      const isTurboBoost = (this.hasTurbo && Math.abs(this.speed) > 1.8) || boosting;
      this.exhaustParticles.push({
        x: exhaustX,
        y: exhaustY,
        vx: -forwardX * (0.6 + Math.random() * 0.5) + (Math.random() - 0.5),
        vy: -forwardY * (0.6 + Math.random() * 0.5) + (Math.random() - 0.5),
        radius: isTurboBoost ? 3.5 + Math.random() * 2.5 : 2.5 + Math.random() * 2,
        alpha: 0.75,
        color: isTurboBoost ? (Math.random() < 0.5 ? '#f97316' : '#38bdf8') : null,
        life: 1
      });
    }

    // Water spray mist kicked up by tires in rain or through puddles
    if ((weather === 'rain' || this.inPuddle) && Math.abs(this.speed) > 0.7 && Math.random() < 0.7) {
      const sprayX = this.x - forwardX * 22 + (Math.random() - 0.5) * 8;
      const sprayY = this.y - forwardY * 22 + (Math.random() - 0.5) * 8;
      this.exhaustParticles.push({
        x: sprayX,
        y: sprayY,
        vx: -forwardX * (0.8 + Math.random() * 0.8) + (Math.random() - 0.5) * 1.6,
        vy: -forwardY * (0.8 + Math.random() * 0.8) + (Math.random() - 0.5) * 1.6,
        radius: 1.8 + Math.random() * 2.2,
        alpha: 0.65,
        color: 'rgba(186, 230, 253, 0.75)',
        life: 0.6
      });
    }

    // Update exhaust particles
    for (let i = this.exhaustParticles.length - 1; i >= 0; i--) {
      const p = this.exhaustParticles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.radius += 0.25;
      p.alpha -= 0.035;
      if (p.alpha <= 0) {
        this.exhaustParticles.splice(i, 1);
      }
    }

    // 7. Speed bump impact (Fragility shock if crossing too fast!)
    const bump = cityMap.checkSpeedBump(this.x, this.y);
    if (bump && this.bumpTimer <= 0) {
      this.bumpTimer = 18; // frames of cooldown
      const impactSeverity = Math.abs(this.speed) / currentMaxSpeed;
      this.bumpOffset = Math.sin(Date.now() * 0.05) * (6 * impactSeverity);
      soundEngine.playBump();
      this.speed *= 0.88;

      // If driving over ~14 km/h (>0.48 speed ratio), trigger pastry shock!
      if (impactSeverity > 0.48 && !shockEvent) {
        shockEvent = {
          type: 'bump',
          severity: (impactSeverity - 0.45) * 2.2,
          message: '⚠️ Drempel te hard genomen!'
        };
      }
    }

    if (this.bumpTimer > 0) {
      this.bumpTimer--;
      this.bumpOffset = Math.sin(this.bumpTimer * 0.8) * 3;
    } else {
      this.bumpOffset = 0;
    }

    // Update sound engine pitch
    const speedFraction = Math.abs(this.speed) / (currentMaxSpeed || 1);
    soundEngine.updateEngine(speedFraction, isThrottling);

    return {
      collision: colResult,
      shockEvent
    };
  }

  getSpeedKmh() {
    // 1 game speed unit = ~8.5 km/h in top-down city scale (~27 km/h top speed)
    return Math.round(Math.abs(this.speed) * 8.5);
  }
}
